import { useState } from 'react';
import { Modal, Button, CheckboxSelections } from '@ds';
import { CloseIcon } from '@ds-icons';
import classNames from 'classnames';
import TransitionImageBal from 'src/assets/images/global-transition-bal.svg';
import TransitionImageBalMobile from 'src/assets/images/global-transition-bal-mobile.svg';
import TransitionImageDeloitte from 'src/assets/images/global-transition-deloitte.svg';
import TransitionImageDeloitteMobile from 'src/assets/images/global-transition-deloitte-mobile.svg';
import TransitionModalHeaderYellow from '@icons/global-instance-transition-modal-header.svg';
import TransitionModalHeaderGreen from '@icons/global-instance-transition-modal-header-green.svg';
import { ButtonSize, ButtonStyle, ButtonVariant } from '@ds/elements/Button/Button.types';
import { DONT_SHOW_GLOBAL_CASE_MODAL_KEY, ImmigrationInstance } from 'src/utils/constants/common';
import { useImmigrationInstanceStore } from 'src/stores/ImmigrationInstanceStore';
import { useThemeStore } from 'src/stores/ThemeStore';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import styles from './GlobalCaseTransitionModal.module.scss';

const LogoImages: Record<string, { mobile: string; desktop: string }> = {
  [ImmigrationInstance.DELOITTE]: {
    mobile: TransitionImageDeloitteMobile,
    desktop: TransitionImageDeloitte,
  },
  [ImmigrationInstance.BAL]: {
    mobile: TransitionImageBalMobile,
    desktop: TransitionImageBal,
  },
};

interface IInstanceInfo {
  redirectMessage: string;
  email: string;
  stayInButtonText: string;
  continueToButtonText: string;
}

export enum GlobalCaseTransitionModalType {
  CASE = 'case',
  QUESTIONNAIRE = 'questionnaire',
  IMMIGRATION_DOCUMENTS = 'immigration documents',
  DOCUMENTS = 'documents',
}

interface IGlobalCaseTransitionModalProps {
  modalType: GlobalCaseTransitionModalType;
  isModalOpen: boolean;
  name?: string;
  onNavigate: () => void;
  onModalClose: () => void;
}

const GlobalCaseTransitionModal = ({
  modalType,
  isModalOpen = false,
  name,
  onNavigate,
  onModalClose,
}: IGlobalCaseTransitionModalProps) => {
  const { isDeloitteTheme } = useThemeStore();
  const { immigrationInstance } = useImmigrationInstanceStore();

  const isMobile = useScreenSize().isMobile;
  const [dontShowAgainSelected, setDontShowAgainSelected] = useState(
    () => sessionStorage.getItem(DONT_SHOW_GLOBAL_CASE_MODAL_KEY) === 'true'
  );
  // Safety check: modal shouldn't be shown before instance config is loaded
  if (!immigrationInstance) {
    return null;
  }
  const InstanceInfo: Record<string, IInstanceInfo> = {
    [ImmigrationInstance.BAL]: {
      redirectMessage: `You are about to be redirected to Deloitte Global Advantage to view your ${
        name ? name + ' ' : ''
      }${modalType}.`,
      email: 'globaladvantagesupport@deloitte.com',
      stayInButtonText: 'STAY IN BAL COBALT',
      continueToButtonText: 'CONTINUE TO GA COBALT',
    },
    [ImmigrationInstance.DELOITTE]: {
      redirectMessage: `You are about to be redirected to BAL to view your ${name ? name + ' ' : ''}${modalType}.`,
      email: 'cobaltsupport@bal.com',
      stayInButtonText: 'STAY IN GA COBALT',
      continueToButtonText: 'CONTINUE TO BAL COBALT',
    },
  };
  const logoImg = LogoImages[immigrationInstance][isMobile ? 'mobile' : 'desktop'];
  const contactToEmail = InstanceInfo[immigrationInstance].email;

  const handleDontShowAgainClick = () => {
    const newValue = !dontShowAgainSelected;
    setDontShowAgainSelected(newValue);

    if (newValue) {
      sessionStorage.setItem(DONT_SHOW_GLOBAL_CASE_MODAL_KEY, 'true');
    } else {
      sessionStorage.removeItem(DONT_SHOW_GLOBAL_CASE_MODAL_KEY);
    }
  };

  const handleContinueButtonClick = () => {
    onModalClose();
    onNavigate();
  };

  return (
    <Modal
      visible={isModalOpen}
      displayCloseIcon={false}
      className={classNames(styles['global-case-modal-container'], {
        [styles['deloitte-theme-global-case-modal-container']]: isDeloitteTheme,
      })}
      modalClassName={styles['global-case-modal']}
    >
      <div className={styles['global-case-transition-header-icon-container']}>
        <img src={TransitionModalHeaderYellow} alt="Global Case Transition" />
        <img src={TransitionModalHeaderGreen} alt="Global Case Transition" />
      </div>
      <div className={styles['global-case-transition-content']}>
        <div>
          {isMobile && (
            <div className={styles['global-case-modal-close-icon']} onClick={onModalClose}>
              <img src={CloseIcon} alt="Close modal" />
            </div>
          )}
          <img src={logoImg} alt="Global Case Transition" />
          <p className={styles['global-case-transition-detail']}>{InstanceInfo[immigrationInstance].redirectMessage}</p>
        </div>
        <div className={styles['transition-actions-container']}>
          <div className={styles['transition-actions']}>
            <Button
              variant={ButtonVariant.outlined}
              styleType={ButtonStyle.secondary}
              size={isMobile ? ButtonSize.xlarge : ButtonSize.medium}
              onClick={onModalClose}
            >
              {InstanceInfo[immigrationInstance].stayInButtonText}
            </Button>
            <Button
              variant={ButtonVariant.contained}
              styleType={ButtonStyle.primary}
              size={isMobile ? ButtonSize.xlarge : ButtonSize.medium}
              onClick={handleContinueButtonClick}
            >
              {InstanceInfo[immigrationInstance].continueToButtonText}
            </Button>
          </div>
          <div className={classNames(styles['pop-up-info-text-container'], styles['legal-info'])}>
            <p className={styles['pop-up-info-text']}>
              Deloitte LLP provides rest of the world immigration services to you under an alliance with BAL LLP.
              Deloitte Tax LLP does not provide immigration or legal services and is not a party to the alliance with
              BAL LLP.
            </p>
          </div>
          <div className={styles['pop-up-info-text-container']}>
            <p className={styles['pop-up-info-text']}>
              Please ensure popups are unblocked on your browser before logging in. Trouble <br></br>logging in?
              Contact&nbsp;
              <a href={`mailto:${contactToEmail}`}>{contactToEmail}</a>
            </p>
          </div>
        </div>
        <div className={styles['dont-show-again-container']}>
          <CheckboxSelections
            className={styles['checkbox']}
            options={[{ label: '', checked: dontShowAgainSelected }]}
            onCheckboxChanged={handleDontShowAgainClick}
          />
          <p className={styles['dont-show-again-text']}>Don't show again</p>
        </div>
      </div>
    </Modal>
  );
};

export default GlobalCaseTransitionModal;
